// useVideoExclusivo — la radio y el vídeo no suenan a la vez.
//
// La regla vive en `stores/player.js`: una sola fuente (`radio` | `video` |
// null) con dos consumidores. Este composable es el cable que faltaba entre esa
// regla y el reproductor de YouTube, y cierra el círculo por los dos lados:
//
//   · el vídeo arranca  → la fuente pasa a `video`, y la radio se calla sola
//   · el vídeo se pausa → la fuente se suelta, y NO se reanuda nada
//   · la radio arranca  → se le pide al vídeo que pare
//
// El segundo y el tercero son nuevos. Antes solo existía el primero, y además
// mal: el clic en la portada llamaba a `stop()` —apagar la radio— en vez de
// declarar que ahora manda el vídeo. La diferencia importa: con `stop()` la
// fuente queda en `null`, así que al darle luego al play de la radio nadie
// sabía que había un vídeo sonando y se quedaban los dos.
//
// 🔴 Lo que NO hace: reanudar. Al pausar el vídeo, la radio se queda callada.
// Encargo de Pablo (27.08.26): pausar algo no puede encender otra cosa, y menos
// después de recargar la página.
import { watch } from "vue";
import { usePlayerStore } from "@/stores/player";

/**
 * @param {import('vue').Ref} refDelVideo El `ref` del VideoFacade que manda en
 *   la página. En la sección TV es el reproductor grande; en la portada, el de
 *   la puerta. Las miniaturas no cuentan: cargan en ese mismo reproductor.
 */
export function useVideoExclusivo(refDelVideo) {
  const playerStore = usePlayerStore();

  // Cuando el reproductor de YouTube dice que ha arrancado —lo diga el clic en
  // la portada o los controles de dentro del iframe—.
  function alReproducirVideo() {
    playerStore.playVideo();
  }

  // Y cuando dice que se ha pausado o ha terminado. Se comprueba que la fuente
  // siga siendo el vídeo: si ya la tiene la radio, es que este `pause` lo hemos
  // provocado nosotros al arrancarla, y soltarla ahí la apagaría de vuelta.
  function alPausarVideo() {
    if (playerStore.isVideoPlaying) playerStore.stop();
  }

  // El otro sentido: alguien da al play de la radio y el vídeo tiene que parar.
  // Se pausa, no se destruye: al volver, el vídeo sigue por donde iba.
  watch(
    () => playerStore.source,
    (fuente) => {
      if (fuente === "radio") refDelVideo.value?.pausar?.();
    },
  );

  return { alReproducirVideo, alPausarVideo };
}
